import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import axiosInstance from "../axiosInstance";

const emptyHouse = {
  name: "",
  code: "",
  colour: "",
  motto: "",
  coordinator: "",
  description: "",
  is_active: true,
};

const emptyCompetition = {
  title: "",
  activity: "Athletics",
  event_date: "",
  venue: "",
  description: "",
  status: "PLANNED",
  notes: "",
};

const emptyResult = {
  house: "",
  position: "",
  points: "",
  award_label: "",
  remarks: "",
};

export default function HousesPage() {
  const [houses, setHouses] = useState([]);
  const [members, setMembers] = useState([]);
  const [competitions, setCompetitions] = useState([]);
  const [options, setOptions] = useState({ students: [], staff: [] });
  const [houseForm, setHouseForm] = useState(emptyHouse);
  const [assign, setAssign] = useState({ student: "", house: "", notes: "" });
  const [competitionForm, setCompetitionForm] = useState(emptyCompetition);
  const [selectedCompetition, setSelectedCompetition] = useState(null);
  const [resultRows, setResultRows] = useState([{ ...emptyResult }]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [h, m, o, c] = await Promise.all([
        axiosInstance.get("houses/"),
        axiosInstance.get("houses/memberships/"),
        axiosInstance.get("houses/options/"),
        axiosInstance.get("houses/competitions/"),
      ]);
      setHouses(h.data);
      setMembers(m.data);
      setOptions(o.data);
      setCompetitions(c.data);
    } catch (error) {
      toast.error("Failed to load houses and inter-house competitions.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const unassigned = useMemo(
    () =>
      options.students.filter(
        (student) => !members.some((member) => member.student === student.id)
      ),
    [options.students, members]
  );

  const saveHouse = async (event) => {
    event.preventDefault();
    try {
      if (houseForm.id) {
        await axiosInstance.patch(`houses/${houseForm.id}/`, houseForm);
      } else {
        await axiosInstance.post("houses/", houseForm);
      }
      toast.success(houseForm.id ? "House updated." : "House added.");
      setHouseForm(emptyHouse);
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save house.");
    }
  };

  const saveAssignment = async (event) => {
    event.preventDefault();
    try {
      await axiosInstance.post("houses/memberships/", assign);
      toast.success("Student assigned to house.");
      setAssign({ student: "", house: "", notes: "" });
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to assign student.");
    }
  };

  const removeAssignment = async (id) => {
    if (!window.confirm("Remove this house assignment?")) return;
    await axiosInstance.delete(`houses/memberships/${id}/`);
    load();
  };

  const saveCompetition = async (event) => {
    event.preventDefault();
    try {
      await axiosInstance.post("houses/competitions/", competitionForm);
      toast.success("Inter-house competition added to Sports & Events.");
      setCompetitionForm(emptyCompetition);
      load();
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Failed to save the competition."
      );
    }
  };

  const openResults = (competition) => {
    setSelectedCompetition(competition);
    setResultRows(
      competition.results?.length
        ? competition.results.map((result) => ({
            house: result.house,
            position: result.position,
            points: result.points,
            award_label: result.award_label,
            remarks: result.remarks,
          }))
        : [{ ...emptyResult }]
    );
  };

  const updateResult = (index, field, value) => {
    setResultRows((current) =>
      current.map((row, rowIndex) =>
        rowIndex === index ? { ...row, [field]: value } : row
      )
    );
  };

  const saveResults = async () => {
    try {
      await axiosInstance.put(
        `houses/competitions/${selectedCompetition.id}/results/`,
        { results: resultRows }
      );
      toast.success("Results saved and the Sports Event marked completed.");
      setSelectedCompetition(null);
      setResultRows([{ ...emptyResult }]);
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save results.");
    }
  };

  return (
    <div className="container-fluid py-4">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
        <div>
          <h4 className="fw-bold mb-1">School Houses & Inter-house Sports</h4>
          <p className="text-muted mb-0">
            Manage house membership, competitions, podium positions and the
            school’s permanent sporting history.
          </p>
        </div>
        <div className="d-flex gap-2">
          <Link className="btn btn-outline-primary" to="/events">
            <i className="bi bi-calendar-event me-2"></i>
            Open Sports & Events
          </Link>
          <button className="btn btn-outline-success" onClick={load}>
            Refresh
          </button>
        </div>
      </div>

      <div className="alert alert-success border-0 shadow-sm">
        Every competition created here is automatically added to{" "}
        <strong>Sports & Events</strong>. Results remain linked to the same
        dated event.
      </div>

      <div className="row g-3 mb-4">
        {houses.map((house) => (
          <div className="col-md-4" key={house.id}>
            <div className="card border-0 shadow-sm h-100">
              <div
                style={{
                  height: 7,
                  background: house.colour || "#125b2d",
                  borderRadius: "0.375rem 0.375rem 0 0",
                }}
              />
              <div className="card-body">
                <div className="d-flex justify-content-between gap-2">
                  <div>
                    <h5 className="fw-bold mb-1">{house.name}</h5>
                    <span className="badge text-bg-light border">
                      {house.code}
                    </span>
                  </div>
                  <span className="badge text-bg-success align-self-start">
                    {house.members_count} children
                  </span>
                </div>
                <p className="text-muted mt-3 mb-1">
                  {house.motto || "No motto added"}
                </p>
                <small>{house.coordinator_name || "No coordinator assigned"}</small>
                <div className="mt-3">
                  <button
                    className="btn btn-sm btn-outline-success"
                    onClick={() => setHouseForm(house)}
                  >
                    Edit House
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="row g-4 mb-5">
        <div className="col-xl-5">
          <div className="card border-0 shadow-sm h-100">
            <form className="card-body row g-3" onSubmit={saveHouse}>
              <h5 className="fw-bold">
                {houseForm.id ? "Edit House" : "Add House"}
              </h5>
              <Field
                label="House Name"
                value={houseForm.name}
                onChange={(value) =>
                  setHouseForm({ ...houseForm, name: value })
                }
              />
              <Field
                label="House Code"
                value={houseForm.code}
                onChange={(value) =>
                  setHouseForm({ ...houseForm, code: value.toUpperCase() })
                }
              />
              <Field
                label="Colour"
                value={houseForm.colour}
                onChange={(value) =>
                  setHouseForm({ ...houseForm, colour: value })
                }
              />
              <Field
                label="Motto"
                value={houseForm.motto}
                onChange={(value) =>
                  setHouseForm({ ...houseForm, motto: value })
                }
              />
              <div className="col-12">
                <label className="form-label fw-semibold">Coordinator</label>
                <select
                  className="form-select"
                  value={houseForm.coordinator || ""}
                  onChange={(event) =>
                    setHouseForm({
                      ...houseForm,
                      coordinator: event.target.value,
                    })
                  }
                >
                  <option value="">Not assigned</option>
                  {options.staff.map((staff) => (
                    <option key={staff.id} value={staff.id}>
                      {staff.full_name} ({staff.user_type})
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-12">
                <label className="form-label fw-semibold">Description</label>
                <textarea
                  className="form-control"
                  value={houseForm.description || ""}
                  onChange={(event) =>
                    setHouseForm({
                      ...houseForm,
                      description: event.target.value,
                    })
                  }
                />
              </div>
              <div className="col-12 d-flex gap-2">
                <button className="btn btn-success" type="submit">
                  Save House
                </button>
                {houseForm.id && (
                  <button
                    className="btn btn-light border"
                    type="button"
                    onClick={() => setHouseForm(emptyHouse)}
                  >
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>

        <div className="col-xl-7">
          <div className="card border-0 shadow-sm h-100">
            <form className="card-body row g-3" onSubmit={saveAssignment}>
              <h5 className="fw-bold">Assign a Child to a House</h5>
              <div className="col-md-6">
                <label className="form-label fw-semibold">Student</label>
                <select
                  className="form-select"
                  required
                  value={assign.student}
                  onChange={(event) =>
                    setAssign({ ...assign, student: event.target.value })
                  }
                >
                  <option value="">Choose student</option>
                  {unassigned.map((student) => (
                    <option key={student.id} value={student.id}>
                      {student.full_name} — {student.student_number || "No number"}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label fw-semibold">House</label>
                <select
                  className="form-select"
                  required
                  value={assign.house}
                  onChange={(event) =>
                    setAssign({ ...assign, house: event.target.value })
                  }
                >
                  <option value="">Choose house</option>
                  {houses
                    .filter((house) => house.is_active)
                    .map((house) => (
                      <option key={house.id} value={house.id}>
                        {house.name}
                      </option>
                    ))}
                </select>
              </div>
              <div className="col-12">
                <Field
                  label="Assignment Notes"
                  value={assign.notes}
                  onChange={(value) => setAssign({ ...assign, notes: value })}
                />
              </div>
              <div className="col-12">
                <button className="btn btn-success" type="submit">
                  Assign Student
                </button>
              </div>
            </form>
            <div className="table-responsive px-3 pb-3">
              <table className="table align-middle">
                <thead>
                  <tr>
                    <th>Student</th>
                    <th>Class</th>
                    <th>House</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {members.map((member) => (
                    <tr key={member.id}>
                      <td>
                        <strong>{member.student_name}</strong>
                        <small className="d-block text-muted">
                          {member.student_number}
                        </small>
                      </td>
                      <td>{member.class_group_name || "—"}</td>
                      <td>{member.house_name}</td>
                      <td className="text-end">
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => removeAssignment(member.id)}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <div className="row g-4 mb-4">
        <div className="col-xl-5">
          <div className="card border-0 shadow-sm">
            <form className="card-body row g-3" onSubmit={saveCompetition}>
              <div>
                <h5 className="fw-bold mb-1">Add Inter-house Competition</h5>
                <p className="small text-muted mb-0">
                  Athletics, soccer, netball and other activities are all
                  supported.
                </p>
              </div>
              <Field
                label="Competition Title"
                value={competitionForm.title}
                onChange={(value) =>
                  setCompetitionForm({ ...competitionForm, title: value })
                }
                required
              />
              <Field
                label="Activity / Sport"
                value={competitionForm.activity}
                onChange={(value) =>
                  setCompetitionForm({ ...competitionForm, activity: value })
                }
                required
              />
              <Field
                label="Date"
                type="date"
                value={competitionForm.event_date}
                onChange={(value) =>
                  setCompetitionForm({ ...competitionForm, event_date: value })
                }
                required
              />
              <Field
                label="Venue"
                value={competitionForm.venue}
                onChange={(value) =>
                  setCompetitionForm({ ...competitionForm, venue: value })
                }
              />
              <div className="col-12">
                <label className="form-label fw-semibold">Status</label>
                <select
                  className="form-select"
                  value={competitionForm.status}
                  onChange={(event) =>
                    setCompetitionForm({
                      ...competitionForm,
                      status: event.target.value,
                    })
                  }
                >
                  <option value="PLANNED">Planned</option>
                  <option value="COMPLETED">Completed</option>
                  <option value="CANCELLED">Cancelled</option>
                </select>
              </div>
              <div className="col-12">
                <label className="form-label fw-semibold">Description</label>
                <textarea
                  className="form-control"
                  rows="3"
                  value={competitionForm.description}
                  onChange={(event) =>
                    setCompetitionForm({
                      ...competitionForm,
                      description: event.target.value,
                    })
                  }
                />
              </div>
              <div className="col-12">
                <button className="btn btn-success" type="submit">
                  Add to Houses and Sports & Events
                </button>
              </div>
            </form>
          </div>
        </div>

        <div className="col-xl-7">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <h5 className="fw-bold">Inter-house Event History</h5>
              <p className="text-muted">
                Open any event to record first, second, third and further
                positions.
              </p>
              {loading ? (
                <div className="text-muted">Loading competitions…</div>
              ) : competitions.length === 0 ? (
                <div className="alert alert-light border">
                  No inter-house competitions recorded yet.
                </div>
              ) : (
                competitions.map((competition) => (
                  <div
                    className="border rounded-3 p-3 mb-3"
                    key={competition.id}
                  >
                    <div className="d-flex flex-wrap justify-content-between gap-3">
                      <div>
                        <h6 className="fw-bold mb-1">{competition.title}</h6>
                        <div className="text-muted small">
                          {competition.activity} · {competition.event_date}
                          {competition.venue ? ` · ${competition.venue}` : ""}
                        </div>
                      </div>
                      <span
                        className={`badge align-self-start ${
                          competition.status === "COMPLETED"
                            ? "text-bg-success"
                            : competition.status === "CANCELLED"
                            ? "text-bg-danger"
                            : "text-bg-warning"
                        }`}
                      >
                        {competition.status}
                      </span>
                    </div>

                    {competition.results?.length > 0 && (
                      <div className="row g-2 mt-2">
                        {competition.results.map((result) => (
                          <div className="col-md-4" key={result.id}>
                            <div className="bg-light rounded-3 p-2 h-100">
                              <strong>
                                {ordinal(result.position)} — {result.house_name}
                              </strong>
                              <small className="d-block text-muted">
                                {result.award_label || "Placed"} ·{" "}
                                {result.points} points
                              </small>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    <button
                      className="btn btn-sm btn-outline-success mt-3"
                      onClick={() => openResults(competition)}
                    >
                      {competition.results?.length
                        ? "Edit Results"
                        : "Record Results"}
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {selectedCompetition && (
        <div className="card border-success shadow-sm mb-4">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-start mb-3">
              <div>
                <h5 className="fw-bold mb-1">
                  Results: {selectedCompetition.title}
                </h5>
                <p className="text-muted mb-0">
                  Enter podium positions, medals or awards, and optional points.
                </p>
              </div>
              <button
                className="btn-close"
                onClick={() => setSelectedCompetition(null)}
                aria-label="Close"
              />
            </div>

            {resultRows.map((row, index) => (
              <div className="row g-2 align-items-end mb-3" key={index}>
                <div className="col-lg-3">
                  <label className="form-label">House</label>
                  <select
                    className="form-select"
                    value={row.house}
                    onChange={(event) =>
                      updateResult(index, "house", event.target.value)
                    }
                  >
                    <option value="">Choose house</option>
                    {houses
                      .filter((house) => house.is_active)
                      .map((house) => (
                        <option key={house.id} value={house.id}>
                          {house.name}
                        </option>
                      ))}
                  </select>
                </div>
                <div className="col-lg-2">
                  <label className="form-label">Position</label>
                  <input
                    className="form-control"
                    type="number"
                    min="1"
                    value={row.position}
                    onChange={(event) =>
                      updateResult(index, "position", event.target.value)
                    }
                  />
                </div>
                <div className="col-lg-2">
                  <label className="form-label">Award</label>
                  <input
                    className="form-control"
                    placeholder="Gold / Silver"
                    value={row.award_label}
                    onChange={(event) =>
                      updateResult(index, "award_label", event.target.value)
                    }
                  />
                </div>
                <div className="col-lg-2">
                  <label className="form-label">Points</label>
                  <input
                    className="form-control"
                    type="number"
                    min="0"
                    step="0.01"
                    value={row.points}
                    onChange={(event) =>
                      updateResult(index, "points", event.target.value)
                    }
                  />
                </div>
                <div className="col-lg-2">
                  <label className="form-label">Remarks</label>
                  <input
                    className="form-control"
                    value={row.remarks}
                    onChange={(event) =>
                      updateResult(index, "remarks", event.target.value)
                    }
                  />
                </div>
                <div className="col-lg-1">
                  <button
                    className="btn btn-outline-danger w-100"
                    onClick={() =>
                      setResultRows((rows) =>
                        rows.filter((_, rowIndex) => rowIndex !== index)
                      )
                    }
                    disabled={resultRows.length === 1}
                  >
                    ×
                  </button>
                </div>
              </div>
            ))}

            <div className="d-flex gap-2">
              <button
                className="btn btn-light border"
                onClick={() =>
                  setResultRows((rows) => [...rows, { ...emptyResult }])
                }
              >
                Add House Position
              </button>
              <button className="btn btn-success" onClick={saveResults}>
                Save Final Results
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required = false }) {
  return (
    <div className="col-md-6">
      <label className="form-label fw-semibold">{label}</label>
      <input
        className="form-control"
        type={type}
        required={required}
        value={value || ""}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  );
}

function ordinal(number) {
  const value = Number(number);
  if (value === 1) return "1st";
  if (value === 2) return "2nd";
  if (value === 3) return "3rd";
  return `${value}th`;
}
